import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import {
  Button,
  Checkbox,
  Drawer,
  Input,
  SearchInput,
  Select,
  Textarea,
  toast,
} from '@/components/ui'
import {
  useCreateEntry,
  useDeleteVariant,
  useLibrary,
  useLibraryEntry,
  useMergeEntries,
  useUpdateEntry,
} from '../api/queries'
import type { AnswerChoice, AnswerType, LibraryEntryDetail } from '../api/types'
import { CHOICE_LABEL, formatDate } from '../utils/meta'

export interface EntryDrawerProps {
  /** An entry id to edit, 'new' to add one, or null when closed. */
  entryId: string | 'new' | null
  canEdit: boolean
  onClose: () => void
}

interface Form {
  canonical_question: string
  answer_type: AnswerType
  answer_choice: AnswerChoice | ''
  answer_text: string
  tags: string
  volatile: boolean
  review_due: string
  active: boolean
}

const EMPTY: Form = {
  canonical_question: '',
  answer_type: 'text',
  answer_choice: '',
  answer_text: '',
  tags: '',
  volatile: false,
  review_due: '',
  active: true,
}

function toForm(entry: LibraryEntryDetail | undefined): Form {
  if (!entry) return EMPTY
  return {
    canonical_question: entry.canonical_question,
    answer_type: entry.answer_type,
    answer_choice: entry.answer_choice ?? '',
    answer_text: entry.answer_text ?? '',
    tags: entry.tags.join(', '),
    volatile: entry.volatile,
    review_due: entry.review_due ?? '',
    active: entry.active,
  }
}

export function EntryDrawer(props: EntryDrawerProps) {
  const isNew = props.entryId === 'new'
  const { data: entry } = useLibraryEntry(props.entryId && !isNew ? props.entryId : null)
  // Keyed on the loaded copy: the form starts from it, and afresh after a save.
  return (
    <EntryDrawerBody
      key={`${props.entryId}:${entry?.updated_at ?? ''}`}
      {...props}
      entry={isNew ? undefined : entry}
    />
  )
}

function EntryDrawerBody({
  entryId,
  canEdit,
  onClose,
  entry,
}: EntryDrawerProps & { entry: LibraryEntryDetail | undefined }) {
  const isNew = entryId === 'new'
  const [form, setForm] = useState<Form>(() => toForm(entry))
  const [mergeSearch, setMergeSearch] = useState('')
  const create = useCreateEntry()
  const update = useUpdateEntry()
  const deleteVariant = useDeleteVariant()
  const merge = useMergeEntries()
  const { data: mergeCandidates } = useLibrary({ q: mergeSearch || undefined })

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  function save() {
    const body = {
      canonical_question: form.canonical_question,
      answer_type: form.answer_type,
      answer_choice: form.answer_type === 'yes_no_na' ? form.answer_choice : ('' as const),
      answer_text: form.answer_text,
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      volatile: form.volatile,
      review_due: form.review_due || null,
      active: form.active,
    }
    const done = {
      onSuccess: () => {
        toast.success('Saved')
        onClose()
      },
      onError: (err: unknown) =>
        toast.danger(`Couldn't save${err instanceof Error ? `: ${err.message}` : ''}`),
    }
    if (isNew) create.mutate(body, done)
    else if (entryId) update.mutate({ id: entryId, ...body }, done)
  }

  const others = (mergeCandidates?.items ?? []).filter((e) => e.id !== entryId).slice(0, 8)

  return (
    <Drawer
      open={entryId !== null}
      onOpenChange={(o) => !o && onClose()}
      size="wide"
      surface="form"
      title={isNew ? 'New library answer' : 'Library answer'}
      meta={
        entry
          ? `Used ${entry.use_count}× · approved ${formatDate(entry.approved_at)}${entry.approved_by ? ` by ${entry.approved_by.split('@')[0]}` : ''}`
          : undefined
      }
      footer={
        canEdit ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!form.canonical_question.trim()}
              loading={create.isPending || update.isPending}
              onClick={save}
            >
              Save
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm">
          Question
          <Textarea
            rows={2}
            readOnly={!canEdit}
            value={form.canonical_question}
            onChange={(e) => set('canonical_question', e.target.value)}
          />
        </label>
        <div className="flex gap-3">
          <label className="flex flex-1 flex-col gap-1 text-sm">
            Kind
            <Select
              disabled={!canEdit}
              value={form.answer_type}
              onChange={(e) => set('answer_type', e.target.value as AnswerType)}
            >
              <option value="text">Free text</option>
              <option value="yes_no_na">Yes / No / N/A</option>
              <option value="field">Header field</option>
            </Select>
          </label>
          {form.answer_type === 'yes_no_na' ? (
            <label className="flex flex-1 flex-col gap-1 text-sm">
              Tick
              <Select
                disabled={!canEdit}
                value={form.answer_choice}
                onChange={(e) => set('answer_choice', e.target.value as AnswerChoice | '')}
              >
                <option value="">—</option>
                {(['yes', 'no', 'na'] as const).map((c) => (
                  <option key={c} value={c}>
                    {CHOICE_LABEL[c]}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}
        </div>
        <label className="flex flex-col gap-1 text-sm">
          Answer
          <Textarea
            rows={6}
            readOnly={!canEdit}
            value={form.answer_text}
            onChange={(e) => set('answer_text', e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Tags
          <Input
            readOnly={!canEdit}
            value={form.tags}
            placeholder="e.g. quality system, calibration"
            onChange={(e) => set('tags', e.target.value)}
          />
        </label>
        <div className="flex flex-wrap items-end gap-4">
          <Checkbox
            disabled={!canEdit}
            checked={form.volatile}
            onChange={(e) => set('volatile', e.target.checked)}
            label="Goes out of date (headcount, inspection dates, vendors…)"
          />
          {form.volatile ? (
            <label className="flex flex-col gap-1 text-sm">
              Check again by
              <Input
                type="date"
                readOnly={!canEdit}
                value={form.review_due}
                onChange={(e) => set('review_due', e.target.value)}
              />
            </label>
          ) : null}
        </div>
        {!isNew ? (
          <Checkbox
            disabled={!canEdit}
            checked={form.active}
            onChange={(e) => set('active', e.target.checked)}
            label="Active — used to answer questionnaires"
          />
        ) : null}

        {entry?.variants.length ? (
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Asked as ({entry.variants.length})</h3>
            {entry.variants.map((v) => (
              <div key={v.id} className="flex items-start gap-2 text-sm">
                <span className="flex-1 text-secondary">{v.question_text}</span>
                {canEdit && entry.variants.length > 1 ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    iconOnly
                    aria-label="Forget this wording"
                    title="Forget this wording (it stops matching this answer)"
                    onClick={() => deleteVariant.mutate({ entryId: entry.id, variantId: v.id })}
                  >
                    <Trash2 size={12} />
                  </Button>
                ) : null}
              </div>
            ))}
          </section>
        ) : null}

        {canEdit && entry ? (
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Merge a duplicate into this answer</h3>
            <p className="text-xs text-secondary">
              The duplicate's wordings move here and it stops being used. This answer's text is
              kept.
            </p>
            <SearchInput
              placeholder="Find the duplicate…"
              value={mergeSearch}
              onChange={(e) => setMergeSearch(e.target.value)}
              aria-label="Find an entry to merge"
            />
            {mergeSearch
              ? others.map((o) => (
                  <div
                    key={o.id}
                    className="flex items-start gap-2 rounded-md border border-subtle p-2 text-sm"
                  >
                    <span className="flex-1">
                      {o.canonical_question}
                      <span className="block text-xs text-tertiary">{o.answer_text}</span>
                    </span>
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={merge.isPending}
                      onClick={() =>
                        merge.mutate(
                          { keepId: entry.id, mergeId: o.id },
                          {
                            onSuccess: (r) => {
                              toast.success(`Merged — ${r.wordings_moved} wordings moved`)
                              setMergeSearch('')
                            },
                            onError: (err) =>
                              toast.danger(err instanceof Error ? err.message : 'Merge failed'),
                          },
                        )
                      }
                    >
                      Merge here
                    </Button>
                  </div>
                ))
              : null}
          </section>
        ) : null}

        {entry?.history.length ? (
          <section className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold">History</h3>
            {[...entry.history].reverse().map((h, i) => (
              <p key={i} className="text-xs text-secondary">
                {formatDate(h.at)} · {h.event.replace(/_/g, ' ')}
                {h.by ? ` · ${h.by.split('@')[0]}` : ''}
                {h.note ? ` · ${h.note}` : ''}
                {h.previous_answer ? (
                  <span className="block text-tertiary">Was: {h.previous_answer}</span>
                ) : null}
              </p>
            ))}
          </section>
        ) : null}
      </div>
    </Drawer>
  )
}
